import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Brand } from "@/components/Brand";
import { OutcomeBadge } from "@/components/OutcomeBadge";
import { getReceipt } from "@/lib/api";

export default function Receipt() {
    const { hash } = useParams();
    const [receipt, setReceipt] = useState(null);
    const [state, setState] = useState("loading"); // loading | ok | missing

    useEffect(() => {
        getReceipt(hash)
            .then((d) => {
                setReceipt(d);
                setState("ok");
            })
            .catch(() => setState("missing"));
    }, [hash]);

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="mx-auto max-w-2xl px-6 py-12">
                <div className="mb-8 flex items-center justify-between">
                    <Brand />
                    <span className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                        Public-safe receipt
                    </span>
                </div>

                {state === "loading" && (
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Loader2 className="h-4 w-4 animate-spin" /> Retrieving verified receipt…
                    </div>
                )}

                {state === "missing" && (
                    <div className="rounded-sm border border-slate-200 bg-white p-8" data-testid="receipt-missing">
                        <h1 className="text-xl font-medium text-slate-900">
                            No verified KATLAS receipt
                        </h1>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600">
                            No verified KATLAS receipt exists for this reference. Receipts
                            are produced only after live signing and verification through
                            the KATLAS node. None are fabricated or simulated.
                        </p>
                        <Link to="/" className="mt-5 inline-block text-sm text-blue-950 underline">
                            Return to overview
                        </Link>
                    </div>
                )}

                {state === "ok" && receipt && (
                    <div className="rounded-sm border border-slate-200 bg-white" data-testid="public-receipt">
                        <div className="flex items-center justify-between border-b border-slate-200 p-6">
                            <div className="flex items-center gap-3">
                                <OutcomeBadge outcome={receipt.outcome} />
                                <span className="text-sm font-medium text-slate-900">
                                    {receipt.lifecycle_action}
                                </span>
                            </div>
                            <span className="inline-flex items-center gap-1.5 text-xs text-green-700">
                                <ShieldCheck className="h-4 w-4" />
                                {receipt.verification_status || "verified"}
                            </span>
                        </div>

                        <dl className="divide-y divide-slate-100 px-6">
                            <Row label="Control decision" value={receipt.outcome} />
                            <Row label="Lifecycle action" value={receipt.lifecycle_action} />
                            {receipt.scenario_type === "corporate_action_election" && (
                                <>
                                    <Row label="Parent event" value={receipt.parent_event_ref} mono />
                                    <Row label="Option" value={receipt.option_ref} mono />
                                    <Row label="Election" value={receipt.election_ref} mono />
                                    <Row label="Decision role" value={receipt.decision_role} />
                                    <Row label="Reason category" value={receipt.reason_category} />
                                    <Row label="Permitted adapter outcome" value={receipt.permitted_adapter_outcome} />
                                </>
                            )}
                            <Row label="Acting role" value={receipt.acting_role} />
                            {receipt.counterparty_role_summary && (
                                <Row label="Counterparty role" value={receipt.counterparty_role_summary} />
                            )}
                            <Row label="Policy ID" value={receipt.policy_id} mono />
                            <Row label="Reason code" value={receipt.reason_code} mono />
                            <Row label="Receipt hash" value={receipt.receipt_hash} mono />
                            <Row label="Signer / attestation" value={receipt.signer_attestation_reference} mono />
                            <Row label="Node timestamp" value={receipt.node_timestamp} mono />
                            <Row label="Verification status" value={receipt.verification_status} />
                            <Row label="Withheld evidence" value={`${receipt.withheld_evidence_count} reference(s)`} />
                        </dl>

                        {receipt.evidence_reference_status && (
                            <div className="border-t border-slate-200 px-6 py-5">
                                <p className="font-mono text-xs uppercase tracking-wide text-slate-400">
                                    Evidence-reference status
                                </p>
                                <ul className="mt-2 space-y-1">
                                    {Object.entries(receipt.evidence_reference_status).map(([k, v]) => (
                                        <li key={k} className="flex items-center justify-between gap-3 text-xs">
                                            <span className="text-slate-600">{k.replace(/_/g, " ")}</span>
                                            <span className="font-mono text-slate-400">{v}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {receipt.shared_evidence_references && (
                            <div className="border-t border-slate-200 px-6 py-5">
                                <p className="font-mono text-xs uppercase tracking-wide text-slate-400">
                                    Shared evidence references
                                </p>
                                <ul className="mt-2 space-y-1">
                                    {receipt.shared_evidence_references.map((e) => (
                                        <li key={e.hash} className="flex items-center justify-between gap-3 text-xs">
                                            <span className="text-slate-600">{e.label}</span>
                                            <span className="font-mono text-slate-400">{e.hash}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="border-t border-slate-200 bg-slate-50 px-6 py-5">
                            <p className="font-mono text-xs uppercase tracking-wide text-slate-400">
                                CAR summary
                            </p>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                {receipt.car_summary}
                            </p>
                        </div>
                    </div>
                )}

                <p className="mt-6 text-xs leading-relaxed text-slate-400">
                    Synthetic demonstration. No raw evidence, private identity data,
                    bearer tokens, certificates or full signed receipt envelopes are
                    exposed on this page.
                </p>
            </div>
        </div>
    );
}

function Row({ label, value, mono }) {
    const text = value == null ? "-"
        : typeof value === "object" ? Object.values(value).filter(Boolean).join(" · ")
        : value;
    return (
        <div className="flex items-start justify-between gap-4 py-3">
            <dt className="text-xs font-mono uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className={`text-right text-sm text-slate-800 ${mono ? "font-mono break-all" : ""}`}>
                {text}
            </dd>
        </div>
    );
}
